import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import { utilityMetersApi, utilityReadingsApi } from '../../services/resources';
import { formatDate, formatNumber, today } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import type { UtilityMeter, UtilityReading } from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: Utility_Meters (the 3 water + 1 electricity meter
// definitions, REQ-029) and Utility_Readings (daily entries, REQ-030) are
// tabs on one page. Consumption is always server-computed as current minus
// previous reading — never accepted from the client (VAL-06) — and a
// rollback (current < previous) is rejected unless is_correction=true with
// a reason, exactly as the friendly-error rule requires; this page surfaces
// that as an optional "Correction" toggle rather than pre-computing a
// client-side preview.

const metersConfig: ModuleConfig<UtilityMeter> = {
  title: 'Meters', entityLabel: 'meter', module: 'Utilities', queryKey: 'utility-meters',
  referenceKeys: ['farms'], api: utilityMetersApi, idKey: 'id',
  defaultValues: { meter_type: 'WATER', status: 'Active' },
  columns: [
    { key: 'code', header: 'Meter Code', primary: true, render: (r) => r.meter_code },
    { key: 'name', header: 'Name', render: (r) => r.meter_name },
    { key: 'type', header: 'Type', render: (r) => r.meter_type },
    { key: 'unit', header: 'Unit', render: (r) => r.unit },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Meter', required: true, optionsKey: 'farms' },
    { name: 'meter_code', label: 'Meter Code', type: 'text', section: 'Meter', required: true, placeholder: 'WM-001 / EM-001' },
    { name: 'meter_name', label: 'Name', type: 'text', section: 'Meter', required: true },
    { name: 'meter_type', label: 'Type', type: 'select', section: 'Meter', required: true, staticOptions: [
      { value: 'WATER', label: 'Water' }, { value: 'ELECTRICITY', label: 'Electricity' },
    ] },
    { name: 'unit', label: 'Unit', type: 'text', section: 'Meter', required: true, placeholder: 'kL / kWh' },
    { name: 'installation_date', label: 'Installation Date', type: 'date', section: 'Meter', optional: true },
    { name: 'status', label: 'Status', type: 'select', section: 'Meter', required: true, staticOptions: [
      { value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' },
    ] },
  ],
};

const readingsConfig: ModuleConfig<UtilityReading> = {
  title: 'Daily Readings', entityLabel: 'reading', module: 'Utilities', queryKey: 'utility-readings',
  referenceKeys: ['utilityMeters'],
  api: {
    list: utilityReadingsApi.list,
    create: utilityReadingsApi.create,
    update: utilityReadingsApi.update,
  },
  idKey: 'id',
  addLabel: 'Record Reading',
  defaultValues: { reading_date: today(), is_correction: false },
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.reading_date) },
    { key: 'value', header: 'Reading', align: 'right', render: (r) => formatNumber(r.reading_value, 3) },
    { key: 'consumption', header: 'Consumption', align: 'right', render: (r) => (r.consumption != null ? formatNumber(r.consumption, 3) : '—') },
    { key: 'correction', header: 'Correction', render: (r) => (r.is_correction ? <ILMSStatusBadge status="Pending" size="sm" /> : '—') },
  ],
  fields: [
    { name: 'meter_id', label: 'Meter', type: 'select', section: 'Reading', required: true, optionsKey: 'utilityMeters' },
    { name: 'reading_date', label: 'Date', type: 'date', section: 'Reading', required: true },
    { name: 'reading_value', label: 'Current Reading', type: 'decimal', section: 'Reading', required: true, min: 0,
      helperText: 'Consumption is calculated automatically on the server as this minus the previous reading.' },
    { name: 'is_correction', label: 'This is a correction (reading rolls back)', type: 'checkbox', section: 'Correction' },
    { name: 'correction_reason', label: 'Correction Reason', type: 'textarea', section: 'Correction', optional: true, fullWidth: true,
      helperText: 'Required if this reading is lower than the previous one.',
      visibleWhen: (v) => !!v.is_correction },
  ],
};

export default function UtilitiesPage() {
  return (
    <TabbedPage
      title="Utilities"
      subtitle="Water and electricity meters with daily readings — consumption is always calculated by the server (REQ-029/030)."
      tabs={[
        { id: 'meters', label: 'Meters', render: () => <ResourceModule config={metersConfig} /> },
        { id: 'readings', label: 'Daily Readings', render: () => <ResourceModule config={readingsConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Utilities', metersConfig, readingsConfig);
